'use client'

import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { HelpCircle, ChevronDown } from 'lucide-react'
import { useState } from 'react'

interface FAQItem {
  question: string
  answer: string
}

interface AutomotiveFAQSectionProps {
  faqs: FAQItem[]
}

export function AutomotiveFAQSection({ faqs }: AutomotiveFAQSectionProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  // FAQPage schema so these answers are eligible for rich results (matches ServiceFAQ pattern)
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map((item) => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  }

  return (
    <section className="relative py-24 overflow-hidden bg-background">
      <script type="application/ld+json">
        {JSON.stringify(faqSchema)}
      </script>
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="max-w-3xl mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="reveal inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#2F5FE8]/10 border border-[#2F5FE8]/20 mb-6"
          >
            <HelpCircle className="h-4 w-4 text-[#2F5FE8]" />
            <span className="eyebrow text-[#2F5FE8]">
              FAQ
            </span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="reveal reveal-delay-1 text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
          >
            Automotive questions we hear every week.
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="reveal reveal-delay-2 text-lg text-muted-foreground"
          >
            Still unsure? These are the operational questions dealers and service shops ask before they switch to Frayze.
          </motion.p>
        </div>

        {/* FAQ Items */}
        <div className="max-w-4xl mx-auto space-y-4">
          {faqs.map((item, index) => (
            <motion.div
              key={item.question}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{
                delay: index * 0.05,
                duration: 0.5,
                ease: "easeOut"
              }}
              className={`reveal ${['reveal-delay-0', 'reveal-delay-1', 'reveal-delay-2', 'reveal-delay-3'][index % 4]} group rounded-2xl border border-border/40 bg-white dark:bg-zinc-900 overflow-hidden transition-all duration-300 hover:shadow-lg`}
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full px-6 py-5 flex items-center justify-between text-left transition-colors duration-300 hover:bg-muted/50"
              >
                <span className="text-base font-semibold text-foreground pr-4">
                  {item.question}
                </span>
                <ChevronDown
                  className={`h-5 w-5 text-muted-foreground flex-shrink-0 transition-transform duration-300 ${
                    openIndex === index ? 'rotate-180' : ''
                  }`}
                />
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === index ? 'max-h-96' : 'max-h-0'
                }`}
              >
                <div className="px-6 pb-5 text-sm text-muted-foreground leading-relaxed">
                  {item.answer}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Footer Link */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="reveal mt-8 text-center text-sm"
        >
          More questions?{" "}
          <Link to="/faq/" className="text-[#2F5FE8] font-semibold underline-offset-4 hover:underline">
            Visit our FAQ →
          </Link>
        </motion.div>
      </div>
    </section>
  )
}
